import { Col, Row, Space, Typography, Modal, Dropdown, message } from "antd";
import { useState } from "react";
import Loader from "../controls/Loader";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import { getTemplatesExport } from "../repository/data.repository";
import TemplatesFormDialog from "../components/TemplatesFormDialog";
import { Template, TemplateVersion } from "../repository/data.default";
import TemplatesView from "../components/TemplatesView";
import TemplateHistoryDrawer from "../components/TemplateHistoryDrawer";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getProblemMessage } from "../utils/http";

function TemplatesPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [templatesInputMode, setTemplatesInputMode] = useState<
    "add" | "update" | "delete"
  >("update");
  const [templatesInputOpen, setTemplatesInputOpen] = useState(false);
  const [templatesInputValues, setTemplatesInputValues] = useState<Template>(
    {} as any,
  );
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTemplate, setHistoryTemplate] = useState<Template | undefined>();
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
  }

  function onTemplatesInputOpen(
    mode: "add" | "update" | "delete",
    values?: Template,
  ): void {
    setTemplatesInputMode(mode);
    setTemplatesInputOpen(true);
    setTemplatesInputValues(values || ({} as any));
  }

  // The grid surfaces "history" alongside update/delete — open the version drawer for that row.
  function onTemplateAction(key: string, record: Template): void {
    if (key === "history") {
      setHistoryTemplate(record);
      setHistoryOpen(true);
      return;
    }
    onTemplatesInputOpen(key as "update" | "delete", record);
  }

  // Restore = load the chosen version's Subject/Body into the edit form; saving snapshots a new version.
  function onRestoreVersion(version: TemplateVersion): void {
    setHistoryOpen(false);
    if (!historyTemplate) return;
    onTemplatesInputOpen("update", {
      ...historyTemplate,
      subject: version.subject,
      body: version.body,
    });
  }

  function onTemplatesInputFinish(): void {
    setTemplatesInputOpen(false);
    setTemplatesInputValues({} as any);
    setTriggerView(Guid.create().toString());
  }

  function onTemplatesInputClose(): void {
    setTemplatesInputOpen(false);
    setTemplatesInputValues({} as any);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading templates export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getTemplatesExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Templates
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Templates", "create")}

            className={`${!permissions.can("Templates", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Templates", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onTemplatesInputOpen("add")}
          >
            Add Template
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <TemplatesView
          trigger={triggerView}
          onAction={onTemplateAction}
          onSearch={onSearch}
        />
      </Col>
      <TemplatesFormDialog
        key={templatesInputMode}
        mode={templatesInputMode}
        open={templatesInputOpen}
        initialValues={templatesInputValues}
        onFinish={onTemplatesInputFinish}
        onCancel={onTemplatesInputClose}
      />
      <TemplateHistoryDrawer
        open={historyOpen}
        template={historyTemplate}
        onClose={() => setHistoryOpen(false)}
        onRestore={onRestoreVersion}
      />
      {contextHolder}
    </Col>
  );
}

export default TemplatesPage;
